import {
	StoreConfigurationTableKey,
	StoreConfigurationTableEntry,
} from './TypeDefinitions';
import { AuthStorageWrapper } from './AuthStorageWrapper';
import {
	storageConstants,
	OIDCClients,
	stringConstants,
	Platform,
} from './AuthConstants';
import { logConstants } from '../utils/Constants';
import { Logger } from './Logger';
import { OIDCConfiguration, StoreConfiguration } from '../Common/TypeDefinitions';
import Utils from '../utils/Utils';
import { SentryLogger } from '../sentryLogging/SentryLogger';
import * as Sentry from '@sentry/browser';

/**
 * Singleton class that manages store configurations and handles operations on the `StoreConfigurations` table in IndexedDB.
 */
export default class StoreConfigManager {
	private static instance: StoreConfigManager;

	static getInstance(): StoreConfigManager {
		if (!StoreConfigManager.instance) {
			StoreConfigManager.instance = new StoreConfigManager();
		}
		return StoreConfigManager.instance;
	}

	/**
	 * Retrieves the store configuration corresponding to the given key.
	 *
	 * @param {StoreConfigurationTableKey} key object of type `StoreConfigurationTableKey`
	 * @returns {(Promise<StoreConfigurationTableEntry> | null)} object of type `StoreConfigurationTableEntry` if the entry was found in the table, `null` otherwise.
	 * @memberof StoreConfigManager
	 */
	async retrieveStoreConfig(
		key: StoreConfigurationTableKey
	): Promise<StoreConfigurationTableEntry> | null {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		const storeConfig = await authStorage.readEntry(
			storageConstants.STORE_CONFIGURATION_TABLE,
			[key.referenceUri]
		);
		if (storeConfig) {
			return storeConfig as StoreConfigurationTableEntry;
		}
		return null;
	}

	/**
	 * Updates the store configurations to the `StoreConfigurations` table in IndexedDB (deletes stale entries as well)
	 *
	 * @param {StoreConfiguration} config object of type `StoreConfiguration`.
	 * @memberof StoreConfigManager
	 */
	async saveStoreConfig(config: StoreConfiguration): Promise<void> {
		Logger.getInstance().write(logConstants.INFO, 'Saving store configurations...');
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		const storeConfigKeys = Object.keys(config);
		storeConfigKeys.forEach(async (refUri) => {
			await authStorage.updateEntry(storageConstants.STORE_CONFIGURATION_TABLE, {
				referenceUri: Utils.getInstance().getSafeUrl(refUri),
				tokenServiceURL: Utils.getInstance().getSafeUrl(config[refUri].tokenServiceURL),
				oidcConfiguration: config[refUri].oidcConfiguration,
				authDomain: config[refUri].authDomain,
				acrValues: config[refUri].acrValues,
				promptLoginEnabled: config[refUri].promptLoginEnabled,
				storeType: config[refUri].storeType,
				defaultGatewayLocation: config[refUri].defaultGatewayLocation,
				storeName: config[refUri].storeName,
				beacon: config[refUri].beacon,
			} as StoreConfigurationTableEntry);
			const breadcrumb = {
				category: 'StoreConfigManager',
				message: `serviceURL: ${config[refUri].tokenServiceURL}`,
				level: Sentry.Severity.Debug,
			};
			SentryLogger.getInstance().addBreadCrumb(breadcrumb);
		});

		const keysFromTable = (await authStorage.readAllKeys(
			storageConstants.STORE_CONFIGURATION_TABLE
		)) as string[][];
		if (keysFromTable) {
			keysFromTable.forEach(async (key) => {
				const refUri = key[0];
				if (!storeConfigKeys.includes(refUri)) {
					await this.deleteStoreConfig({
						referenceUri: refUri,
					});
				}
			});
		}
	}

	/**
	 * Deletes the store configuration corresponding to the given key.
	 *
	 * @param {StoreConfigurationTableKey} key object of type `StoreConfigurationTableKey`.
	 * @memberof StoreConfigManager
	 */
	async deleteStoreConfig(key: StoreConfigurationTableKey): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		Logger.getInstance().write(
			logConstants.INFO,
			'Deleting store configuration for reference uri = ' + key.referenceUri
		);
		await authStorage.deleteEntry(storageConstants.STORE_CONFIGURATION_TABLE, [
			key.referenceUri,
		]);
	}

	/**Gets all store config table entries
	 * @returns an array of StoreConfigurationTableEntry
	 * @memberof StoreConfigManager
	 */
	async retrieveAllStoreConfigTableEntries(): Promise<StoreConfigurationTableEntry[]> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();

		//Fetch the configuration table entries to get the authdomain and referenceuri combinations
		const listOfStoreConfigTableEntries = (await authStorage.readAll(
			storageConstants.STORE_CONFIGURATION_TABLE
		)) as StoreConfigurationTableEntry[];

		return listOfStoreConfigTableEntries;
	}

	/**Gets all store config table entries
	 * @returns an JSON of StoreConfiguration
	 * @memberof StoreConfigManager
	 */
	async retrieveAllStoreConfigTableEntriesAsJSON(): Promise<StoreConfiguration> {
		const listOfStoreConfigTableEntries = await this.retrieveAllStoreConfigTableEntries();

		const storeConfigJSON: StoreConfiguration = {};
		if (listOfStoreConfigTableEntries) {
			listOfStoreConfigTableEntries.forEach((entry: StoreConfigurationTableEntry) => {
				storeConfigJSON[Utils.getInstance().getSafeUrl(entry.referenceUri)] = {
					tokenServiceURL: Utils.getInstance().getSafeUrl(entry.tokenServiceURL),
					authDomain: entry.authDomain,
					oidcConfiguration: entry.oidcConfiguration,
					acrValues: entry.acrValues,
					promptLoginEnabled: entry.promptLoginEnabled,
					storeType: entry.storeType,
					defaultGatewayLocation: entry.defaultGatewayLocation,
					storeName: entry.storeName,
					beacon: entry.beacon,
				};
			});
		}
		return storeConfigJSON;
	}

	parseStoreConfig(config: string): StoreConfiguration {
		const configurationJSON = JSON.parse(config);
		const storeConfigKeys = Object.keys(configurationJSON);
		storeConfigKeys.forEach((refUri) => {
			if (configurationJSON[refUri].oidcConfiguration) {
				configurationJSON[refUri].oidcConfiguration = JSON.parse(
					configurationJSON[refUri].oidcConfiguration
				);
			}
			configurationJSON[refUri].tokenServiceURL = Utils.getInstance().getSafeUrl(
				configurationJSON[refUri].tokenServiceURL
			);

			//If the safeUrl version is not the same as the url
			//replace the url with safeUrl version
			const safeRefUri = Utils.getInstance().getSafeUrl(refUri);
			if (safeRefUri != refUri) {
				configurationJSON[safeRefUri] = configurationJSON[refUri];
				delete configurationJSON[refUri];
			}
		});
		return configurationJSON;
	}

	/**
	 *  Retrive client Id from OIDCConfiguration for a give platform
	 *
	 * @param {OIDCConfiguration} oidcConfig object of type `OIDCConfiguration`.
	 * @param platform object of type string
	 * @memberof StoreConfigManager
	 */
	getClientid(oidcConfig: OIDCConfiguration, platform: Platform): string {
		if (oidcConfig && oidcConfig.oidc_clients && oidcConfig.oidc_clients[platform]) {
			const clients = oidcConfig.oidc_clients[platform].filter((client) => {
				return client.name === OIDCClients[platform];
			});

			if (clients.length > 0) {
				return clients[0].id;
			}
		}
		Logger.getInstance().write(
			logConstants.ERROR,
			stringConstants['CLIENT_ID_NOT_PRESENT']
		);
		throw new Error(stringConstants['CLIENT_ID_NOT_PRESENT']);
	}
}
